import React, { useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Icon, Note, Popover } from '@repo/design-system';
import type { AuthoredOpeningHoursVersion } from '@repo/frontend-types';
import { space } from '@repo/design-tokens';

import { visuallyHidden } from '@repo/ui';

import {
  draftFields,
  standardEditLimits,
  standardEditNote,
  standardEditProblem,
  standardName,
  standardNameProblem,
  type StandardDraft,
  type StandardFields,
} from './hoursModel';
import StandardHoursFields from './StandardHoursFields';
import { actionProblem, HOURS_STILL_SAVING, type HoursActionResult } from './useOpeningHoursEditor';
import { usePopoverProblem } from './usePopoverProblem';

/**
 * *Edit details* (spec opening-hours-edit-standard-details, R3): the trigger under a running or
 * upcoming standard set's card, and its popover — option A of decision 1 on the review page, approved
 * by Rob on 2026-11-02. A running set changes its name and end; an upcoming set its name, start and
 * end. One Save sends one change, and the set next to it moves with a shared boundary in the same
 * save; the note says which before Save is pressed.
 *
 * Built as *New Standard Hours From a Date* is, sharing its fields (`StandardHoursFields`), with
 * `usePopoverProblem` for an answer that lands after the popover closed.
 */

export interface EditStandardHoursProps {
  calendar: AuthoredOpeningHoursVersion;
  versions: readonly AuthoredOpeningHoursVersion[];
  /** The twin's date (`launchHours.today`). */
  today: string;
  /** A save is waiting or running: the action waits (R3.8). */
  busy: boolean;
  onChange: (
    calendar: AuthoredOpeningHoursVersion,
    fields: StandardFields,
  ) => Promise<HoursActionResult>;
  /**
   * Saved: the step follows the set and says what changed (R3.6), and returns focus here unless the
   * person closed the popover while it saved (`focus: false`) and so has moved on.
   */
  onChanged: (
    before: AuthoredOpeningHoursVersion,
    fields: StandardFields,
    options: { focus: boolean },
  ) => void;
  /** An outcome that arrived after the popover was closed, for the step to show and say. */
  onLateProblem: (sentence: string) => void;
  /** Whether the popover is open, so the step can tell when the set it edits goes (Failure States). */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether a change is on its way, so the step does not take its own move for someone else's. It
   * goes false after the outcome is reported, so the step can tell an answer that took the set away.
   */
  onSending?: (sending: boolean) => void;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  className?: string;
}

export const EDIT_HOURS_CHANGED =
  'The hours changed while you were editing these. Check the dates and save again.';
export const EDIT_HOURS_UNKNOWN = 'Couldn’t tell whether the change was saved.';
/** The set being edited went with someone else's change, and its popover with it. */
export const EDIT_HOURS_GONE = 'These hours were changed or removed by someone else.';

const draftOf = (calendar: AuthoredOpeningHoursVersion): StandardDraft => ({
  name: standardName(calendar),
  from: calendar.effectiveDateFrom,
  to: calendar.effectiveDateTo,
  noEnd: calendar.effectiveDateTo === null,
});

const EditStandardHours: React.FC<EditStandardHoursProps> = ({
  calendar,
  versions,
  today,
  busy,
  onChange,
  onChanged,
  onLateProblem,
  onOpenChange,
  onSending,
  triggerRef: givenTriggerRef,
  className,
}) => {
  const ownTriggerRef = useRef<HTMLButtonElement>(null);
  const triggerRef = givenTriggerRef ?? ownTriggerRef;
  const popoverId = useId();
  const reasonId = useId();
  const holdId = useId();
  const idPrefix = `edit-standard-${useId().replace(/:/g, '')}`;
  const popover = usePopoverProblem(onLateProblem);
  const { open, problem: error, report } = popover;
  const [draft, setDraft] = useState<StandardDraft>(() => draftOf(calendar));
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  /** The person closed the popover while it saved: a saved answer then leaves focus where they went. */
  const dismissedRef = useRef(false);

  // The limits and the note from the sets as they are now: a read landing while the popover is open
  // keeps the fields as typed and recomputes these (Failure States).
  const limits = standardEditLimits(versions, calendar, today, draft.from ?? undefined);
  const fields = draftFields(draft);
  const nameProblem = standardNameProblem(draft.name);
  const note = fields ? standardEditNote(versions, calendar, fields, today).sentences : [];
  const hold = standardEditProblem(versions, calendar, draft, today);

  const show = () => {
    setDraft(draftOf(calendar));
    popover.show();
    onOpenChange?.(true);
  };

  const close = () => {
    if (sendingRef.current) dismissedRef.current = true;
    popover.close();
    onOpenChange?.(false);
  };

  const save = async () => {
    if (!fields || hold !== null || sendingRef.current) return;
    sendingRef.current = true;
    dismissedRef.current = false;
    setSending(true);
    onSending?.(true);
    report(null);
    const result = await onChange(calendar, fields);
    sendingRef.current = false;
    setSending(false);
    const problem = actionProblem(result, EDIT_HOURS_CHANGED, EDIT_HOURS_UNKNOWN);
    if (problem === null) {
      const focus = !dismissedRef.current;
      close();
      onChanged(calendar, fields, { focus });
    } else {
      report(problem);
    }
    // Last, so a step that finds the set gone says so once, over this popover's own outcome.
    onSending?.(false);
  };

  // Its own save waits too once the popover is closed: reopening would show the stored values while
  // the answer still to come is about the ones sent.
  const waitReason = (busy || sending) && !(sending && open) ? HOURS_STILL_SAVING : null;

  return (
    <span className={className}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        iconLeft={<Icon name="pen" />}
        aria-disabled={waitReason !== null || undefined}
        aria-describedby={waitReason ? reasonId : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        title={waitReason ?? undefined}
        onClick={() => {
          if (waitReason === null && !open) show();
        }}
      >
        Edit details
      </Button>
      {waitReason && (
        <span id={reasonId} style={visuallyHidden}>
          {waitReason}
        </span>
      )}
      <Popover
        id={popoverId}
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        title="Edit standard hours"
        // Focus lands on the first field, *Name* (Popover's default).
        placement="bottom"
        width={360}
        actions={
          <>
            <Button variant="frosted" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              aria-disabled={hold !== null || sending || undefined}
              aria-describedby={hold ? holdId : undefined}
              loading={sending}
              onClick={() => void save()}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="body">
          <StandardHoursFields
            idPrefix={idPrefix}
            name={draft.name}
            onName={name => {
              setDraft(d => ({ ...d, name }));
              report(null);
            }}
            nameProblem={nameProblem}
            fixedStart={limits.startEditable ? null : calendar.effectiveDateFrom}
            from={draft.from}
            to={draft.to}
            noEnd={draft.noEnd}
            onDates={dates => {
              setDraft(d => ({ ...d, ...dates }));
              report(null);
            }}
            start={{ min: limits.start.min, max: draft.noEnd ? limits.start.max : limits.end.max }}
            end={limits.end}
            noEndReason={limits.noEnd.reason}
            today={today}
            disabled={sending}
            note={note}
          />
          {hold && (
            <span id={holdId} style={visuallyHidden}>
              {hold}
            </span>
          )}
          {/* Polite, so the outcome is heard while focus stays on Save (R3.7, criterion 18). */}
          <div role="status">
            {error && (
              <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
                {error}
              </Note>
            )}
          </div>
        </div>
      </Popover>
    </span>
  );
};

export default styled(EditStandardHours)`
  display: flex;

  .body {
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
  }
`;
